/* ============================================================================
   Bulk & Corporate Gifting — shared runtime
   Ported from the Claude Design prototype's DCLogic component (17 Aug 2026).

   One script serves every mes-bulk-* section on the page. Each section emits
   its own <script> tag, so everything here is behind an idempotency guard —
   the second and later executions return immediately.

   The design was one React component with shared state; here the sections are
   separate Liquid renders, so the state they shared (the visitor's "brief")
   lives in ONE place — localStorage key `mes-gifting-profile`, same key and
   shape as the prototype — and every section that personalises subscribes to
   it through data-mbo-* attributes. No section talks to another directly.

   Analytics events go to window.dataLayer with the prototype's exact event
   names, so the GTM plan in the handoff README works unchanged.
   ============================================================================ */
/* ---- H335 stage-1 lead tags (17 Sep 2026) --------------------------------------------------------------
   Builds the customer tags for one bulk-orders enquiry, VERBATIM from store-data's pinned convention
   (library/chains/2026-09-17-b2b-lead-capture.md §Interface, commit e833879f). Pure: no DOM, no network, so
   whichever capture mechanism Shrey picks calls the same function. Tags come from option VALUES, never labels
   (the option labelled "10 to 50" submits "Under 50"). An unknown value maps to other / unknown and is never
   invented. No personal data ever goes into a tag. If the form's option values change, §Interface's tables and
   the two maps below change in the SAME commit, or the leads page mis-buckets. */
(function () {
  'use strict';
  if (window.MesB2BTags) return;
  var OCCASION = { 'Diwali': 'diwali', 'Onboarding': 'onboarding', 'Client gifting': 'client-gifting', 'Rewards': 'rewards', 'Wedding favours': 'wedding-favours', 'Other': 'other' };
  var QUANTITY = { 'Under 10': 'under-10', 'Under 50': 'under-50', '50 to 100': '50-100', '100 to 250': '100-250', '250 to 500': '250-500', '500+': '500-plus' };
  window.MesB2BTags = function (fields) {
    var f = fields || {};
    var tags = ['corporate-lead', 'b2b-stage:new', 'b2b-src:bulk-orders-form'];
    var occ = String(f.occasion || '').trim();
    if (occ) { tags.push('b2b-occasion:' + (Object.prototype.hasOwnProperty.call(OCCASION, occ) ? OCCASION[occ] : 'other')); }
    var qty = String(f.quantity || '').trim();
    if (qty) { tags.push('b2b-qty:' + (Object.prototype.hasOwnProperty.call(QUANTITY, qty) ? QUANTITY[qty] : 'unknown')); }
    var ad = String(f.adRef || '').toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
    tags.push('b2b-ad:' + (ad || 'none'));
    return tags;
  };
})();

(function () {
  'use strict';
  if (window.MesBulk) return;

  var LS_KEY = 'mes-gifting-profile';

  function track(name, meta) {
    var payload = { event: name, t: new Date().toISOString() };
    if (meta) for (var k in meta) payload[k] = meta[k];
    (window.dataLayer = window.dataLayer || []).push(payload);
  }

  function readProfile() {
    try {
      var p = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (p && typeof p === 'object') return { occasion: p.occasion || '', band: p.band || '', budget: p.budget || '' };
    } catch (e) { /* storage blocked: the page simply stays unpersonalised */ }
    return { occasion: '', band: '', budget: '' };
  }
  function writeProfile(p) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch (e) {}
  }
  function clearProfile() {
    try { localStorage.removeItem(LS_KEY); } catch (e) {}
  }

  var state = {
    p: readProfile(),
    briefStep: 0,
    submittedRecap: null
  };

  function hasProfile() { return !!(state.p.occasion || state.p.band || state.p.budget); }
  function belowMoq() { return hasProfile() && state.p.band === 'Under 10'; }

  /* the lowercase occasion word the prototype used in personalised copy */
  var OCCASION_WORD = {
    'Diwali': 'Diwali', 'Onboarding': 'onboarding', 'Client gifting': 'client gifting',
    'Rewards': 'rewards', 'Wedding favours': 'wedding favours'
  };

  function briefLineText() {
    var w = OCCASION_WORD[state.p.occasion] || '';
    return [
      w ? w.charAt(0).toUpperCase() + w.slice(1) : null,
      state.p.band ? state.p.band + ' pcs' : null,
      state.p.budget && state.p.budget !== 'Not sure yet' ? state.p.budget : null
    ].filter(Boolean).join(' · ');
  }

  /* ---- personalisation fan-out -------------------------------------------
     Any element can opt in with:
       data-mbo-text="hero-eyebrow|hero-sub|form-heading|form-intro|sticky-title|brief-hint"
     The element's own rendered content is its default; personalised copy is
     rebuilt from the profile the way the prototype's renderVals() did. */
  function applyText() {
    var w = OCCASION_WORD[state.p.occasion] || '';
    document.querySelectorAll('[data-mbo-text]').forEach(function (el) {
      if (!el.dataset.mboDefault) el.dataset.mboDefault = el.textContent;
      var d = el.dataset.mboDefault;
      switch (el.dataset.mboText) {
        case 'hero-eyebrow':
          el.textContent = hasProfile() && w ? 'Your ' + w + ' gifting, planned below' : d; break;
        case 'hero-sub':
          el.textContent = hasProfile()
            ? 'Kits, prices and lead times for ' + (w || 'your gifting') + (state.p.band ? ' at ' + state.p.band + ' pieces' : '') + '.'
            : d;
          break;
        case 'form-heading':
          el.textContent = hasProfile() ? 'Your inquiry, half filled' : d; break;
        case 'form-intro':
          el.textContent = belowMoq()
            ? (el.dataset.mboIntroBelowMoq || d)
            : hasProfile() ? (el.dataset.mboIntroBrief || d) : d;
          break;
        case 'sticky-title':
          el.textContent = belowMoq() ? 'Under 10 pieces · buy online' : d; break;
        case 'brief-hint':
          el.textContent = hasProfile() && briefLineText() ? briefLineText() : d; break;
      }
    });
  }

  /* kits: match badge + border against the chosen occasion */
  function applyKits() {
    document.querySelectorAll('[data-mbo-kit-occasion]').forEach(function (card) {
      var m = hasProfile() && card.dataset.mboKitOccasion === state.p.occasion;
      card.dataset.matched = m ? 'true' : 'false';
    });
  }

  /* form mirroring — the brief writes into the enquiry form fields */
  function applyForm() {
    var form = document.querySelector('form.mbo-form');
    if (!form) return;
    var occ = form.querySelector('[name="contact[Occasion]"]');
    var qty = form.querySelector('[name="contact[Quantity]"]');
    var notes = form.querySelector('[name="contact[body]"]');
    if (occ && state.p.occasion) occ.value = state.p.occasion;
    if (qty && state.p.band) qty.value = state.p.band;
    if (notes && state.p.budget && state.p.budget !== 'Not sure yet' && !notes.value.trim()) {
      notes.value = 'Budget per gift: ' + state.p.budget + '.';
    }
    if (occ && !state.p.occasion) { /* cleared brief leaves manual choices alone */ }
  }

  /* brief strip: step states + chip row */
  function applyBrief() {
    var strip = document.querySelector('[data-mbo-brief]');
    if (!strip) return;
    var defs = [
      { key: 'occasion', filled: !!state.p.occasion },
      { key: 'band', filled: !!state.p.band },
      { key: 'budget', filled: !!state.p.budget }
    ];
    strip.querySelectorAll('[data-mbo-step]').forEach(function (btn, i) {
      btn.dataset.state = i === state.briefStep ? 'active' : (defs[i] && defs[i].filled ? 'filled' : 'empty');
    });
    strip.querySelectorAll('[data-mbo-chiprow]').forEach(function (row, i) {
      row.hidden = i !== state.briefStep;
      row.querySelectorAll('.mbo-chip').forEach(function (chip) {
        var key = row.dataset.mboChiprow;
        chip.setAttribute('aria-pressed', state.p[key] === chip.dataset.mboValue ? 'true' : 'false');
      });
    });
    var clearBtn = strip.querySelector('[data-mbo-clear]');
    if (clearBtn) clearBtn.hidden = !hasProfile();
  }

  function applyAll() {
    applyText(); applyKits(); applyForm(); applyBrief();
  }

  function setBrief(key, val) {
    track('brief_' + key, { value: val });
    var same = state.p[key] === val;
    state.p[key] = same ? '' : val;
    if (!same) state.briefStep = Math.min(state.briefStep + 1, 2);
    writeProfile(state.p);
    applyAll();
  }

  function scrollToForm() {
    var el = document.getElementById('quote');
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
  }

  function goQuote(source, notesPatch) {
    track('cta_get_quote', { source: source });
    if (notesPatch) {
      var notes = document.querySelector('form.mbo-form [name="contact[body]"]');
      if (notes) notes.value = notesPatch;
    }
    scrollToForm();
  }

  /* ---- wiring ---- */
  function wire() {
    /* every quote CTA */
    document.querySelectorAll('[data-mbo-quote-cta]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        goQuote(el.dataset.mboQuoteCta, el.dataset.mboNotes || null);
      });
    });

    /* WhatsApp catalogue CTA: personalise the prefilled text at click time */
    document.querySelectorAll('[data-mbo-wa]').forEach(function (el) {
      el.addEventListener('click', function () {
        track('catalogue_whatsapp', { occasion: state.p.occasion, band: state.p.band });
        var base = el.dataset.mboWaText || '';
        var msg = base +
          (state.p.occasion ? ' We are looking at ' + state.p.occasion + '.' : '') +
          (state.p.band ? ' Around ' + state.p.band + ' pieces.' : '');
        el.href = 'https://wa.me/' + el.dataset.mboWaNumber + '?text=' + encodeURIComponent(msg);
      });
    });

    /* brief chips + steps + clear */
    var strip = document.querySelector('[data-mbo-brief]');
    if (strip) {
      strip.querySelectorAll('[data-mbo-step]').forEach(function (btn, i) {
        btn.addEventListener('click', function () { state.briefStep = i; applyBrief(); });
      });
      strip.querySelectorAll('[data-mbo-chiprow]').forEach(function (row) {
        row.querySelectorAll('.mbo-chip').forEach(function (chip) {
          chip.addEventListener('click', function () { setBrief(row.dataset.mboChiprow, chip.dataset.mboValue); });
        });
      });
      var clearBtn = strip.querySelector('[data-mbo-clear]');
      if (clearBtn) clearBtn.addEventListener('click', function () {
        track('brief_cleared');
        state.p = { occasion: '', band: '', budget: '' };
        state.briefStep = 0;
        clearProfile();
        var form = document.querySelector('form.mbo-form');
        if (form) {
          var occ = form.querySelector('[name="contact[Occasion]"]'); if (occ) occ.value = '';
          var qty = form.querySelector('[name="contact[Quantity]"]'); if (qty) qty.value = '';
          var notes = form.querySelector('[name="contact[body]"]');
          if (notes && /^Budget per gift:/.test(notes.value.trim())) notes.value = '';
        }
        applyAll();
      });
    }

    /* announcement rotation — text swap, no layout motion, so it runs under
       reduced-motion too (the foil shimmer is what the media query stops) */
    var ann = document.querySelector('[data-mbo-ann]');
    if (ann) {
      var msgs;
      try { msgs = JSON.parse(ann.dataset.mboAnnMsgs || '[]'); } catch (e) { msgs = []; }
      if (msgs.length > 1) {
        var idx = 0;
        setInterval(function () {
          idx = (idx + 1) % msgs.length;
          ann.textContent = msgs[idx];
        }, 4200);
      }
    }

    /* The just-submitted signal: form.posted_successfully? does not survive the
       return_to redirect (18 Aug 2026, Priyanka's defect A3), so the redirect carries
       ?sent=1 and the client renders the success state from it. */
    var wasSent = /[?&]sent=1(&|$)/.test(location.search);

    /* sticky CTA bar */
    var sticky = document.querySelector('[data-mbo-sticky]');
    if (sticky) {
      var submitted = wasSent;
      var update = function () {
        sticky.dataset.show = (window.scrollY > 560 && !submitted) ? 'true' : 'false';
      };
      window.addEventListener('scroll', update, { passive: true });
      update();
    }

    /* ---- the enquiry form --------------------------------------------------
       A real Shopify contact form: the POST goes to Shopify, which emails the
       store and applies its own spam protection. The prototype's client-side
       validation (including the work-email rule) runs first; only a clean
       form is allowed to POST. The success recap is saved to sessionStorage
       at submit time because the reload that follows a Shopify form POST
       carries no field values back. */
    /* ---- ad attribution + occasion pre-fill (Gary, 18 Aug 2026) ------------
       ?ad=<id> (fallback: utm_content) is captured into sessionStorage on landing
       so it survives on-page navigation, and written into the hidden
       contact[Ad reference] input. With NO ad id the input is DISABLED — a
       disabled input neither submits nor validates, so the field structurally
       cannot gate submit (the 15 Jul hidden-required lesson).
       ?occasion=<value> pre-selects the occasion dropdown IF the value matches
       an option exactly; anything else is a silent no-op by design. Regex query
       parse, not URLSearchParams — this file targets old Android. */
    var qp = function (name) {
      var m = location.search.match(new RegExp('[?&]' + name + '=([^&#]*)'));
      if (!m) return '';
      try { return decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { return ''; }
    };
    var adRef = qp('ad') || qp('utm_content');
    if (adRef) { try { sessionStorage.setItem('mes-gifting-ad', adRef); } catch (e) {} }
    else { try { adRef = sessionStorage.getItem('mes-gifting-ad') || ''; } catch (e) {} }
    var adInput = document.querySelector('[data-mbo-ad-ref]');
    if (adInput) {
      if (adRef) { adInput.value = adRef; } else { adInput.disabled = true; }
    }
    var occParam = qp('occasion');
    if (occParam) {
      var occSel = document.querySelector('#mbo-occasion');
      if (occSel) {
        for (var oi = 0; oi < occSel.options.length; oi++) {
          if (occSel.options[oi].value === occParam) { occSel.value = occParam; break; }
        }
      }
    }

    var form = document.querySelector('form.mbo-form');
    if (form) {
      var FIELDS = ['name', 'email', 'company', 'phone', 'quantity', 'occasion'];
      var control = function (key) { return form.querySelector('[data-mbo-field="' + key + '"] .mbo-form__control'); };
      var fieldWrap = function (key) { return form.querySelector('[data-mbo-field="' + key + '"]'); };

      FIELDS.forEach(function (key) {
        var c = control(key);
        if (c) c.addEventListener('input', function () { delete fieldWrap(key).dataset.error; });
        if (c) c.addEventListener('change', function () { delete fieldWrap(key).dataset.error; });
      });

      var validate = function () {
        var e = {};
        var val = function (k) { var c = control(k); return c ? c.value : ''; };
        if (!val('name').trim()) e.name = 'Please tell us your name.';
        var em = val('email').trim();
        if (!em) e.email = 'Please enter your work email.';
        else if (!/^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/.test(em)) e.email = 'That email does not look right. Check the spelling.';
        else if (/@(gmail|yahoo|hotmail|outlook|rediffmail)\./i.test(em)) e.email = 'Please use your work email so we can quote for your company.';
        if (!val('company').trim()) e.company = 'Please enter your company name.';
        var ph = val('phone').replace(/[^0-9]/g, '');
        if (!ph) e.phone = 'Please enter a phone number.';
        else if (ph.length < 10) e.phone = 'Please enter a 10 digit phone number.';
        if (!val('quantity')) e.quantity = 'Please choose a quantity.';
        if (!val('occasion')) e.occasion = 'Please choose an occasion.';
        return e;
      };

      /* ⛔ DOCUMENT-LEVEL CAPTURE, not a listener on the form (Priyanka, 19 Aug 2026:
         "I only entered name and email… the details were still saved and showed Got it,
         although it displayed an error"). A form-level listener cannot guarantee a block:
         Shopify's own spam-protection script also listens for submit, and when it takes over
         it re-submits the form PROGRAMMATICALLY — and a programmatic form.submit() fires no
         submit event, so our preventDefault never applies and the invalid data posts anyway.
         Capture on document runs before every other listener, and stopImmediatePropagation on
         an invalid form means Shopify's handler never sees the event, so nothing can re-submit
         it. (This never reproduced in our own browser because the captcha script is blocked
         there — which is exactly why her report outranked our testing three times running.) */
      document.addEventListener('submit', function (ev) {
        if (ev.target !== form) { return; }
        var errors = validate();
        FIELDS.forEach(function (key) {
          var w = fieldWrap(key);
          if (!w) return;
          if (errors[key]) {
            w.dataset.error = 'true';
            var msg = w.querySelector('.mbo-form__error');
            if (msg) msg.textContent = errors[key];
          } else {
            delete w.dataset.error;
          }
        });
        var bad = FIELDS.filter(function (k) { return errors[k]; });
        if (bad.length) {
          ev.preventDefault();
          ev.stopImmediatePropagation();   /* nothing downstream may re-submit this */
          track('lead_form_error', { fields: bad.join(',') });
          var first = control(bad[0]);
          if (first) {
            /* Priyanka, 19 Aug 2026: "page jumping to top on error". A bare .focus() scrolls the
               field into view from wherever the browser decides — measured at 19,523px of jump
               from a submit press. Focus WITHOUT scrolling, then only bring the field into view
               if it is actually off-screen, and gently. (Distinct from the success-path jump
               fixed in V9.21.131 — that one was a layout collapse; this is focus scrolling.) */
            try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
            var r = first.getBoundingClientRect();
            var offScreen = r.top < 80 || r.bottom > (window.innerHeight || 0) - 20;
            if (offScreen) { first.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
          }
          return;
        }
        var v = function (k) { var c = control(k); return c ? c.value : ''; };
        track('lead_form_submit', { quantity: v('quantity'), occasion: v('occasion'), company: v('company') });
        /* H412 (28 Sep): ONE id for this enquiry, shared by the browser Lead (Meta eventID) and the server-side
           Conversions API Lead that Linus's service sends, so Meta counts it once. Generated here because the
           browser never learns the Shopify customer id the server creates. */
        var leadEventId = 'lead_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
        try {
          sessionStorage.setItem('mes-gifting-sent', JSON.stringify({
            eventId: leadEventId,
            name: v('name').trim().split(' ')[0],
            summary: v('quantity') + ' pieces · ' + v('occasion') + ' · ' + v('company').trim(),
            /* raw values for the ad-platform lead events fired on the success render (H330) */
            occasion: v('occasion'),
            quantity: v('quantity')
          }));
        } catch (e2) {}
        /* ---- H335 lead capture (Shrey 1A, A481, 17 Sep 2026) -------------------------------------
           A clean enquiry is ALSO handed to Ted's engine, which creates or tags the Shopify customer
           (tags per §Interface, email marketing left NOT subscribed). It rides a beacon, which the
           browser delivers even though this very submit navigates the page away; text/plain avoids a
           CORS preflight. It never blocks or delays the native POST: the store email, the ?sent=1
           thank-you page and the Ad reference field (Harshil's and Pushpa's conversion events) are
           exactly as before, and if the engine is down the enquiry still arrives by email. */
        var endpointEl = document.querySelector('[data-mbo-lead-endpoint]');
        var endpoint = endpointEl ? endpointEl.getAttribute('data-mbo-lead-endpoint') : '';
        if (endpoint && window.MesB2BTags) {
          try {
            var email = v('email').trim();
            var adField = form.querySelector('[data-mbo-ad-ref]');
            var ad = adField && !adField.disabled ? adField.value : '';
            var notes = form.querySelector('[name="contact[body]"]');
            var lead = {
              v: 1,
              source: 'bulk-orders-form',
              email: email,
              name: v('name').trim(),
              company: v('company').trim(),
              phone: v('phone').trim(),
              quantity: v('quantity'),
              occasion: v('occasion'),
              adRef: ad,
              notes: notes ? notes.value.trim() : '',
              tags: window.MesB2BTags({ occasion: v('occasion'), quantity: v('quantity'), adRef: ad }),
              page: location.pathname,
              submittedAt: new Date().toISOString(),
              eventId: leadEventId,
              test: /@example\.com$/i.test(email)
            };
            window.__mboLastLead = lead;
            var body = JSON.stringify(lead);
            var sent = false;
            if (navigator.sendBeacon) { sent = navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' })); }
            if (!sent && window.fetch) { fetch(endpoint, { method: 'POST', mode: 'no-cors', keepalive: true, headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: body }).catch(function () {}); }
            track('lead_capture_sent', { transport: sent ? 'beacon' : 'fetch' });
          } catch (e3) { /* capture is best-effort; the native enquiry below always goes */ }
        }
        /* no preventDefault: the clean form POSTs to Shopify, spam protection included */
      }, true);
    }

    /* Success state. The SWAP + recap now happen in an inline script inside the section,
       synchronously during parse — see mes-bulk-quote.liquid for why (Priyanka, 19 Aug 2026:
       doing it from this deferred file collapsed the layout after the browser had already
       jumped to #quote, so the page bounced to the top and smooth-scrolled back down).
       window.__mboSent is that script's signal. All that is left here is cleaning the URL,
       and NO scroll: the browser's own #quote anchor now lands on the final layout. */
    /* LEAD EVENTS (Gary, H330, 16 Sep 2026). The Meta pixel had fired zero Lead events in 30
       days and Google had no lead conversion, while both platforms are about to optimise for
       this enquiry. Fired ONCE per success render, on the same moment the success card shows,
       guarded on each global existing (both are injected by the Google & YouTube channel and
       the Meta app, not by this theme). The dataLayer `lead_form_submit` push above is untouched.
       The recap comes from the section's inline script (window.__mboSentRecap), which reads and
       clears sessionStorage synchronously during parse, before this deferred file runs. */
    /* H412 (28 Sep): each platform's global is injected by ITS app, asynchronously, and this page defines no stub.
       The old code fired only if the global already existed at this moment, and skipped silently otherwise, so on a
       slow phone (Instagram's in-app browser, where ad clicks land) the Lead could be lost with no error. Now each
       call waits for its global, every 250ms for up to 20s, then gives up quietly. */
    function whenReady(name, fn) {
      var tries = 0;
      (function attempt() {
        if (typeof window[name] === 'function') { try { fn(window[name]); } catch (e) {} return; }
        if (++tries < 80) { setTimeout(attempt, 250); }
      })();
    }
    function fireLeadEvents(recap) {
      if (window.__mboLeadFired) return;
      window.__mboLeadFired = true;
      var occasion = (recap && recap.occasion) || '';
      var band = (recap && recap.quantity) || '';
      var eventId = (recap && recap.eventId) || '';
      whenReady('fbq', function (fbq) {
        if (eventId) { fbq('track', 'Lead', { content_name: 'b2b-enquiry', content_category: occasion }, { eventID: eventId }); }
        else { fbq('track', 'Lead', { content_name: 'b2b-enquiry', content_category: occasion }); }
      });
      whenReady('gtag', function (gtag) { gtag('event', 'b2b_enquiry', { occasion: occasion, quantity: band }); });
      /* ChatGPT Ads (V9.28.34): same moment, same once-only guard */
      whenReady('oaiq', function (oaiq) { oaiq('measure', 'lead_created', { type: 'customer_action' }); });
    }

    var success = document.querySelector('[data-mbo-success]');
    var sentFb = null;
    if (success && wasSent) {
      if (!window.__mboSent) {
        /* inline script absent or blocked: fall back to the old behaviour rather than
           leaving the visitor staring at the form they just submitted */
        success.hidden = false;
        var formWrapFb = document.querySelector('[data-mbo-form-wrap]');
        if (formWrapFb) formWrapFb.hidden = true;
        try { sentFb = JSON.parse(sessionStorage.getItem('mes-gifting-sent') || 'null'); } catch (e) {}
        var nameElFb = success.querySelector('[data-mbo-sent-name]');
        var sumElFb = success.querySelector('[data-mbo-sent-summary]');
        var recapFb = success.querySelector('[data-mbo-recap]');
        if (sentFb) {
          if (nameElFb) nameElFb.textContent = 'Got it, ' + sentFb.name + '.';
          if (sumElFb) sumElFb.textContent = sentFb.summary;
        } else if (recapFb) {
          recapFb.hidden = true;
          if (nameElFb) nameElFb.textContent = 'Got it.';
        }
        try { sessionStorage.removeItem('mes-gifting-sent'); } catch (e) {}
        scrollToForm();
      }
      fireLeadEvents(window.__mboSentRecap || sentFb);
      /* drop ?sent=1 so a refresh or a shared link shows the form, not a stale success. H412: NOT immediately. The
         Meta pixel's own PageView goes out ~6-7s after load on live (measured 28 Sep), and the "PageView with sent=1"
         conversion reads the address then; stripping it at load meant that conversion could never match. Wait for
         the pixel plus 8s, or 20s at most. */
      var cleanUrl = function () { try { history.replaceState(null, '', location.pathname + location.hash); } catch (e) {} };
      var cleaned = false;
      var cleanOnce = function () { if (!cleaned) { cleaned = true; cleanUrl(); } };
      whenReady('fbq', function () { setTimeout(cleanOnce, 8000); });
      setTimeout(cleanOnce, 20000);
    }

    applyAll();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();

  window.MesBulk = { track: track, goQuote: goQuote };
})();
